import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Shield, Calendar, ChevronRight, ChevronLeft, Clock, Tag } from 'lucide-react'
import { blogPosts, BlogPost } from '../data/blogPosts'

const BlogPostPage = () => {
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()

  // Find the post
  const post = blogPosts.find(p => p.slug === slug)

  // Find related posts (same category, exclude current post)
  const relatedPosts = post
    ? blogPosts.filter(p => p.slug !== post.slug && p.category === post.category).slice(0, 2)
    : []

  // Set page title and meta description for SEO
  useEffect(() => {
    if (post) {
      document.title = `${post.title} | TrustCenterPage.com`

      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]')
      if (!metaDescription) {
        metaDescription = document.createElement('meta')
        metaDescription.setAttribute('name', 'description')
        document.head.appendChild(metaDescription)
      }
      metaDescription.setAttribute('content', post.metaDescription)
    }
  }, [post])

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <button
            onClick={() => navigate('/blog')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Back to Blog
          </button>
        </div>
      </div>
    )
  }

  // Simple markdown-to-JSX converter for blog content
  const renderMarkdownContent = (content: string) => {
    const lines = content.split('\n')
    const elements: React.ReactNode[] = []
    let currentParagraph: string[] = []
    let listItems: string[] = []
    let inList = false

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const text = currentParagraph.join(' ')
        elements.push(
          <p key={elements.length} className="mb-4 text-gray-700 leading-relaxed">
            {parseInlineMarkdown(text)}
          </p>
        )
        currentParagraph = []
      }
    }

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={elements.length} className="mb-6 space-y-2 list-disc list-inside text-gray-700">
            {listItems.map((item, i) => (
              <li key={i} className="ml-4">{parseInlineMarkdown(item)}</li>
            ))}
          </ul>
        )
        listItems = []
        inList = false
      }
    }

    lines.forEach((line, index) => {
      // H1
      if (line.startsWith('# ')) {
        flushParagraph()
        flushList()
        elements.push(
          <h1 key={elements.length} className="text-3xl font-bold text-gray-900 mb-4 mt-8">
            {line.substring(2)}
          </h1>
        )
      }
      // H2
      else if (line.startsWith('## ')) {
        flushParagraph()
        flushList()
        elements.push(
          <h2 key={elements.length} className="text-2xl font-bold text-gray-900 mb-3 mt-6">
            {line.substring(3)}
          </h2>
        )
      }
      // H3
      else if (line.startsWith('### ')) {
        flushParagraph()
        flushList()
        elements.push(
          <h3 key={elements.length} className="text-xl font-semibold text-gray-900 mb-3 mt-4">
            {line.substring(4)}
          </h3>
        )
      }
      // List items
      else if (line.trim().match(/^[-*]\s/)) {
        flushParagraph()
        inList = true
        listItems.push(line.trim().substring(2))
      }
      // Empty line
      else if (line.trim() === '') {
        flushParagraph()
        flushList()
      }
      // Regular paragraph text
      else {
        if (inList) {
          flushList()
        }
        currentParagraph.push(line.trim())
      }
    })

    // Flush any remaining content
    flushParagraph()
    flushList()

    return elements
  }

  // Parse inline markdown (bold, italic, links)
  const parseInlineMarkdown = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = []
    let remaining = text
    let key = 0

    while (remaining.length > 0) {
      // Bold text **text**
      const boldMatch = remaining.match(/\*\*([^*]+)\*\*/)
      if (boldMatch && boldMatch.index !== undefined) {
        if (boldMatch.index > 0) {
          parts.push(remaining.substring(0, boldMatch.index))
        }
        parts.push(<strong key={key++} className="font-semibold">{boldMatch[1]}</strong>)
        remaining = remaining.substring(boldMatch.index + boldMatch[0].length)
        continue
      }

      // Italic text *text*
      const italicMatch = remaining.match(/\*([^*]+)\*/)
      if (italicMatch && italicMatch.index !== undefined) {
        if (italicMatch.index > 0) {
          parts.push(remaining.substring(0, italicMatch.index))
        }
        parts.push(<em key={key++} className="italic">{italicMatch[1]}</em>)
        remaining = remaining.substring(italicMatch.index + italicMatch[0].length)
        continue
      }

      // Links [text](url)
      const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/)
      if (linkMatch && linkMatch.index !== undefined) {
        if (linkMatch.index > 0) {
          parts.push(remaining.substring(0, linkMatch.index))
        }
        parts.push(
          <a
            key={key++}
            href={linkMatch[2]}
            className="text-primary-600 hover:text-primary-700 underline"
            target={linkMatch[2].startsWith('http') ? '_blank' : undefined}
            rel={linkMatch[2].startsWith('http') ? 'noopener noreferrer' : undefined}
          >
            {linkMatch[1]}
          </a>
        )
        remaining = remaining.substring(linkMatch.index + linkMatch[0].length)
        continue
      }

      // No more matches, add remaining text
      parts.push(remaining)
      break
    }

    return parts
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">TrustCenterPage.com</h1>
            </div>
            <div className="flex items-center gap-6">
              <a href="/directory" className="text-gray-700 hover:text-primary-600 font-medium">Directory</a>
              <a href="/pricing" className="text-gray-700 hover:text-primary-600 font-medium">Pricing</a>
              <a href="/admin" className="text-gray-700 hover:text-primary-600 font-medium">Company Login</a>
            </div>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back to Blog Link */}
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium mb-6"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back to Blog
          </button>

          {/* Article Header */}
          <header className="mb-8">
            {/* Category */}
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700">
                {post.category}
              </span>
              {post.featured && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-700">
                  Featured
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
              <div className="flex items-center gap-2">
                <span className="font-medium">{post.author}</span>
              </div>
              <span>•</span>
              <span>{new Date(post.publishDate).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}</span>
              <span>•</span>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{post.readTime}</span>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 pb-6 border-b border-gray-200">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full"
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                </span>
              ))}
            </div>
          </header>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            {renderMarkdownContent(post.content)}
          </div>

          {/* CTA Box */}
          <div className="mt-12 p-6 bg-primary-50 border border-primary-200 rounded-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Ready to Build Your Trust Center?
            </h3>
            <p className="text-gray-700 mb-4">
              Start showcasing your security and compliance documentation to close deals faster.
            </p>
            <div className="flex gap-3 flex-wrap">
              <a
                href="https://calendar.app.google/mtvgJVaXPtfD95dcA"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book a Demo
              </a>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center px-4 py-2 border border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 font-medium"
              >
                Sign In
                <ChevronRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost) => (
                <article
                  key={relatedPost.slug}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                >
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-3">
                    {relatedPost.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 hover:text-primary-600">
                    {relatedPost.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {relatedPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{relatedPost.readTime}</span>
                    <span className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center">
                      Read More
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Shield className="h-6 w-6 text-primary-400" />
                <span className="ml-2 text-lg font-semibold">TrustCenterPage.com</span>
              </div>
              <p className="text-gray-400">
                Your security is our priority. Access comprehensive documentation
                and evidence of our security practices.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/blog" className="hover:text-white">Blog</a></li>
                <li><a href="/pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/status" className="hover:text-white">Status Page</a></li>
                <li><a href="/privacy" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white">Terms of Service</a></li>
                <li><a href="/dpa" className="hover:text-white">Data Processing Agreement</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Security Team: security@company.com</li>
                <li>Privacy Team: privacy@company.com</li>
                <li>Support: support@company.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Company Name. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default BlogPostPage
